if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

/* =========================================================
   PRODUCT IMAGE VIEWER
   - Desktop: click opens a centered enlarged-image modal.
   - Mobile: swipe left/right between images without closing.
   - Mobile: native two-finger pinch zoom remains available.
   ========================================================= */

(function () {
  const SWIPE_DISTANCE = 45;

  function getModalItems(modal) {
    const content = modal.querySelector('.product-media-modal__content');
    if (!content) return [];
    return Array.from(content.children).filter((item) => item.hasAttribute('data-media-id'));
  }

  function setActiveModalItem(modal, requestedMediaId) {
    const items = getModalItems(modal);
    if (!items.length) return;

    const normalizedId = String(requestedMediaId || '').split('-').pop();
    let index = items.findIndex((item) => String(item.dataset.mediaId) === normalizedId);

    if (index < 0) {
      index = items.findIndex((item) => String(item.dataset.mediaId).endsWith(normalizedId));
    }

    if (index < 0) {
      index = items.findIndex((item) => item.classList.contains('active'));
    }

    if (index < 0) index = 0;

    showModalItem(modal, index);
  }

  function showModalItem(modal, index) {
    const items = getModalItems(modal);
    if (!items.length) return;

    const wrappedIndex = (index + items.length) % items.length;

    items.forEach((item, itemIndex) => {
      item.classList.toggle('active', itemIndex === wrappedIndex);
      item.setAttribute('aria-hidden', itemIndex === wrappedIndex ? 'false' : 'true');
    });

    modal.dataset.activeMediaIndex = String(wrappedIndex);

    const content = modal.querySelector('.product-media-modal__content');
    if (content) {
      content.scrollTop = 0;
      content.scrollLeft = 0;
    }

    const counter = modal.querySelector('.product-media-modal__counter');
    if (counter) counter.textContent = `${wrappedIndex + 1} / ${items.length}`;

    const previous = modal.querySelector('.product-media-modal__nav--previous');
    const next = modal.querySelector('.product-media-modal__nav--next');
    const hideNavigation = items.length < 2;

    if (previous) previous.hidden = hideNavigation;
    if (next) next.hidden = hideNavigation;
    if (counter) counter.hidden = hideNavigation;

    const activeItem = items[wrappedIndex];
    const deferredMedia = activeItem.querySelector('.deferred-media');
    if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
      deferredMedia.loadContent(false);
    }
  }

  function moveModal(modal, direction) {
    const items = getModalItems(modal);
    if (items.length < 2) return;

    const currentIndex = Number.parseInt(modal.dataset.activeMediaIndex || '0', 10);
    showModalItem(modal, currentIndex + direction);
  }

  function addModalControls(modal) {
    if (modal.dataset.imageViewerReady === 'true') return;
    modal.dataset.imageViewerReady = 'true';

    const dialog = modal.querySelector('.product-media-modal__dialog');
    const content = modal.querySelector('.product-media-modal__content');
    if (!dialog || !content) return;

    const previous = document.createElement('button');
    previous.type = 'button';
    previous.className = 'product-media-modal__nav product-media-modal__nav--previous';
    previous.setAttribute('aria-label', 'Previous image');
    previous.innerHTML = '&#10094;';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'product-media-modal__nav product-media-modal__nav--next';
    next.setAttribute('aria-label', 'Next image');
    next.innerHTML = '&#10095;';

    const counter = document.createElement('div');
    counter.className = 'product-media-modal__counter';
    counter.setAttribute('aria-live', 'polite');

    [previous, next].forEach((button) => {
      button.addEventListener('pointerup', (event) => event.stopPropagation());
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
      });
    });

    previous.addEventListener('click', () => moveModal(modal, -1));
    next.addEventListener('click', () => moveModal(modal, 1));

    dialog.appendChild(previous);
    dialog.appendChild(next);
    dialog.appendChild(counter);

    let touchStartX = 0;
    let touchStartY = 0;
    let singleTouch = false;
    let multiTouch = false;

    content.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length === 1) {
          touchStartX = event.touches[0].clientX;
          touchStartY = event.touches[0].clientY;
          singleTouch = true;
          multiTouch = false;
        } else {
          singleTouch = false;
          multiTouch = true;
        }
      },
      { passive: true }
    );

    content.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length > 1) {
          multiTouch = true;
          singleTouch = false;
        }
      },
      { passive: true }
    );

    content.addEventListener(
      'touchend',
      (event) => {
        if (!singleTouch || multiTouch || event.changedTouches.length !== 1) {
          singleTouch = false;
          multiTouch = false;
          return;
        }

        const endTouch = event.changedTouches[0];
        const deltaX = endTouch.clientX - touchStartX;
        const deltaY = endTouch.clientY - touchStartY;

        singleTouch = false;

        if (Math.abs(deltaX) < SWIPE_DISTANCE) return;
        if (Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;

        moveModal(modal, deltaX < 0 ? 1 : -1);
      },
      { passive: true }
    );

    modal.addEventListener('keydown', (event) => {
      if (!modal.hasAttribute('open')) return;

      if (event.code === 'ArrowLeft') {
        event.preventDefault();
        moveModal(modal, -1);
      }

      if (event.code === 'ArrowRight') {
        event.preventDefault();
        moveModal(modal, 1);
      }
    });

    const observer = new MutationObserver(() => {
      if (!modal.hasAttribute('open')) return;

      const activeItem = getModalItems(modal).find((item) => item.classList.contains('active'));
      setActiveModalItem(modal, activeItem ? activeItem.dataset.mediaId : '');
    });

    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
  }

  function initializeProductImageViewers(root = document) {
    root.querySelectorAll('product-modal.product-media-modal').forEach(addModalControls);
  }

  /* Capture product-image clicks before the theme's in-frame magnifier runs. */
  document.addEventListener(
    'click',
    (event) => {
      const imageOpener = event.target.closest('.product__modal-opener--image');
      if (!imageOpener || imageOpener.closest('product-modal')) return;

      const button =
        imageOpener.querySelector('.product__media-toggle') ||
        imageOpener.closest('button') ||
        imageOpener;

      const modalOpener = imageOpener.closest('modal-opener');
      const modalSelector = modalOpener ? modalOpener.getAttribute('data-modal') : null;
      const modal = modalSelector ? document.querySelector(modalSelector) : document.querySelector('product-modal');

      if (!modal || typeof modal.show !== 'function') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const mediaItem = imageOpener.closest('[data-media-id]');
      const mediaId = mediaItem ? mediaItem.dataset.mediaId : '';

      addModalControls(modal);
      modal.show(button);

      window.requestAnimationFrame(() => {
        setActiveModalItem(modal, mediaId);
      });
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeProductImageViewers());
  } else {
    initializeProductImageViewers();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeProductImageViewers(event.target);
  });
})();